import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "use-debounce";
import { infiniteScrollHook } from "../../hooks/infiniteScrollHook";
import {
  EntityType,
  ServiceRequestService,
  ServiceRequestEntityTypesEnum,
} from "@propertelligent/client-api";
import ServiceRequestTopHeader from "../../newComponents/listings/top-listing-headers/service-requests-listing-top-header";
import ServiceRequestCard from "../../newComponents/listings/listing-cards/service-requests";
import AddServiceRequestForm from "../../newComponents/serviceRequest/AddServiceRequestForm";
import { Route, Routes, useLocation } from "react-router-dom";
import ServiceRequestDetails from "./ServiceRequestDetails";
import { UserLoginData } from "../../components/common/charts/Enums";
import apiHelper from "../../utils/apiHelper";
import { usePermissions } from "../../hooks/usePermissions";
import NoAccess from "../NoAccess";
import { PermissionGuard } from "../../newComponents/PermissionGuard";
import { AccessLevelEnum } from "@propertelligent/client-api";
import { isTenantOrContractorRole } from "../../utils/roleGuards";
import { ALL_FILTER_KEY, resolveAllFilterCount, useAggregateFilterCounts, resolveOrphanedAggregateFilter, shouldResolveOrphanedAggregateFilter } from "../../hooks/useAggregateFilterCounts";
import {
  getApiServiceRequestOrganizationlistCounts,
  getApiServiceRequestPropertylistCounts,
  getApiServiceRequestUnitlistCounts,
  getApiServiceRequestTenantlistCounts,
  getApiServiceRequestContractorlistCounts,
} from "../../services/listFilterCounts.service";
import { escapeDynamicLinqStringLiteral } from "../../utils/dynamicLinq";

interface ServiceRequestProps {
  isEntity?: boolean;
  entityTypeId?: EntityType | any;
  entityId?: number;
  className?: string;
  parentClassName?: string;
  onServiceRequestAdded?: () => void; // Add the callback prop
  userRole?: "Landlord" | "Tenant" | "Contractor";
  tenantUnitId?: number | null;
}

const ServiceRequest: React.FC<ServiceRequestProps> = ({
  isEntity = false,
  entityTypeId,
  entityId,
  className = "",
  onServiceRequestAdded,
  userRole = "Landlord",
  parentClassName,
  tenantUnitId,
}) => {
  const { hasAccess, canWrite } = usePermissions();
  const { search, pathname } = useLocation();
  const queryParams = new URLSearchParams(search);
  const initialSelectedOption = queryParams.get("selectedOption") || ALL_FILTER_KEY;

  const getFilterString = (filter: string, search: string) => {
    let baseFilter = "";
    switch (filter) {
      case "New":
        baseFilter = "ServiceRequestStatus == 1 || ServiceRequestStatus == 2";
        break;
      case "In Progress":
        baseFilter = "ServiceRequestStatus == 3";
        break;
      case "Fixed":
        baseFilter = "ServiceRequestStatus == 4 || ServiceRequestStatus == 5";
        break;
      case "Completed":
        baseFilter = "ServiceRequestStatus == 6";
        break;
      default:
        baseFilter = "";
        break;
    }
    const searchFilter = search
      ? `Description.ToLower().Contains("${escapeDynamicLinqStringLiteral(search.toLowerCase())}")`
      : "";
    return [baseFilter ? `(${baseFilter})` : "", searchFilter].filter(Boolean).join(" && ");
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [activeFilter, setActiveFilter] = useState(initialSelectedOption);
  const [isModalOpen, setModalOpen] = useState(false);
  const [hasAnyRequests, setHasAnyRequests] = useState(false);

  const isFirstRender = useRef(true);


  useEffect(() => {
    const params = new URLSearchParams(search);
    const option = params.get("selectedOption") || ALL_FILTER_KEY;
    setActiveFilter(option);
  }, [search]);
  const handleSort = (newDirection: "asc" | "desc") => {
    setSortDirection(newDirection);
  };

  useEffect(() => {
  if (isFirstRender.current) {
    isFirstRender.current = false;
    return;
  }

  if (!pathname.includes("servicerequestdetails")) {
    fetchData(1);
    // Non-entity scope already re-fetches counts via the scopeKey-change effect inside
    // useAggregateFilterCounts (scopeKey embeds `pathname` for that case) -- forcing a
    // second refreshCounts() here would fire a redundant dual live+baseline fetch on top of
    // it. The entity-scoped case's scopeKey does not include pathname, so it still needs an
    // explicit refresh here when returning from a details sub-route.
    if (isEntity) {
      refreshCounts();
    }
  }
}, [pathname, isEntity]);



  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
  };

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const searchOnlyFilter = useMemo(
    () =>
      debouncedSearchTerm
        ? `Description.ToLower().Contains("${escapeDynamicLinqStringLiteral(debouncedSearchTerm.toLowerCase())}")`
        : null,
    [debouncedSearchTerm],
  );
  const rowsFilter = useMemo(
    () => getFilterString(activeFilter, debouncedSearchTerm) || null,
    [activeFilter, debouncedSearchTerm],
  );

  const getServiceRequestListFn = () => {

    if (isEntity && entityTypeId !== undefined && entityId !== undefined) {
      if (entityTypeId === EntityType.Property) {
        return (
          _: number,
          pageSize?: number,
          pageNumber?: number,
          skipValue?: number,
          filterEntityTypeId?: any,
          filterEntityIds?: any,
          fullyQualifiedFilters?: string,
          fullyQualifiedSorts?: string,
        ) =>
          ServiceRequestService.getApiServiceRequestPropertylist(
            entityId,
            pageSize,
            pageNumber,
            skipValue,
            filterEntityTypeId,
            filterEntityIds,
            fullyQualifiedFilters,
            fullyQualifiedSorts,
          );
      } else if (entityTypeId === EntityType.Unit) {
        return (
          _: number,
          pageSize?: number,
          pageNumber?: number,
          skipValue?: number,
          filterEntityTypeId?: any,
          filterEntityIds?: any,
          fullyQualifiedFilters?: string,
          fullyQualifiedSorts?: string,
        ) =>
          ServiceRequestService.getApiServiceRequestUnitlist(
            entityId,
            pageSize,
            pageNumber,
            skipValue,
            filterEntityTypeId,
            filterEntityIds,
            fullyQualifiedFilters,
            fullyQualifiedSorts,
          );
      } else if (entityTypeId == EntityType.Tenant) {
        return (
          _: number,
          pageSize?: number,
          pageNumber?: number,
          skipValue?: number,
          filterEntityTypeId?: any,
          filterEntityIds?: any,
          fullyQualifiedFilters?: string,
          fullyQualifiedSorts?: string,
        ) =>
          ServiceRequestService.getApiServiceRequestTenantlist(
            entityId,
            pageSize,
            pageNumber,
            skipValue,
            filterEntityTypeId,
            filterEntityIds,
            fullyQualifiedFilters,
            fullyQualifiedSorts,
          );
      } else if (entityTypeId == EntityType.Contractor) {
        return (
          _: number,
          pageSize?: number,
          pageNumber?: number,
          skipValue?: number,
          filterEntityTypeId?: any,
          filterEntityIds?: any,
          fullyQualifiedFilters?: string,
          fullyQualifiedSorts?: string,
        ) =>
          ServiceRequestService.getApiServiceRequestContractorlist(
            entityId,
            pageSize,
            pageNumber,
            skipValue,
            filterEntityTypeId,
            filterEntityIds,
            fullyQualifiedFilters,
            fullyQualifiedSorts,
          );
      }
      // Add more entity type cases here as needed
    }

    return ServiceRequestService.getApiServiceRequestOrganizationlist;
  };

  const {
    rows,
    loading,
    hasMore,
    rowCount,
    fetchData,
    currentPage,
  } = infiniteScrollHook({
    httpFetchDataFn: getServiceRequestListFn(),
    FullyQualifiedFilters: rowsFilter,
    pageSize: 10,
    FullyQualifiedSorts: `RequestDate ${sortDirection}`,
  });

  const fetchCounts = useCallback(async (filter: string | null) => {
    const orgId = parseInt(sessionStorage.getItem(UserLoginData.organization_id) || "0");
    // Falls back to the tenantInfo blob (mirrors infiniteScrollHook.tsx) -- on a tenant's first-ever
    // portal visit the standalone tenant_id key can lag behind tenantInfo (e.g. ApplicationStatusHeader's
    // handleGoToDashboard only refreshes tenantInfo), which otherwise silently skips this fetch entirely.
    const tenantId =
      parseInt(sessionStorage.getItem(UserLoginData.tenant_id) || "0") ||
      Number(JSON.parse(sessionStorage.getItem("tenantInfo") || "{}").tenantId) ||
      0;
    const contractorInfo = JSON.parse(sessionStorage.getItem("contractorInfo") || "{}");
    const contractorId = Number(
      contractorInfo?.id ?? contractorInfo?.contractorId ?? sessionStorage.getItem("contractorId") ?? 0
    );
    const reqID = orgId || tenantId || contractorId;

    if (!reqID) return undefined;

    try {
        let apiCall = getApiServiceRequestOrganizationlistCounts;
        let callEntityId = reqID;

        if (isEntity && entityTypeId !== undefined && entityId !== undefined) {
          callEntityId = entityId;
             if (entityTypeId === EntityType.Property) apiCall = getApiServiceRequestPropertylistCounts;
             else if (entityTypeId === EntityType.Unit) apiCall = getApiServiceRequestUnitlistCounts;
             else if (entityTypeId === EntityType.Tenant) apiCall = getApiServiceRequestTenantlistCounts;
             else if (entityTypeId === EntityType.Contractor) apiCall = getApiServiceRequestContractorlistCounts;
        }

        const res = await apiHelper(
          apiCall,
          { showNotification: false },
          callEntityId,
          1,
          1,
          undefined,
          undefined,
          undefined,
          filter,
          undefined,
        );
        return res?.data?.aggregateValues ?? undefined;
      } catch (e) {
        console.error("Error fetching global aggregates:", e);
        return undefined;
    }
  }, [entityId, entityTypeId, isEntity]);

  const {
    aggregateValues: globalAggregateValues,
    baselineAggregateValues,
    loading: countsLoading,
    refreshCounts,
    getVisibleOptions,
  } = useAggregateFilterCounts({
    fetchCounts,
    searchOnlyFilter,
    scopeKey: `service-requests:${isEntity ? `${entityTypeId}:${entityId}` : pathname}`,
  });

  const totalGlobalCount = useMemo(
    () => resolveAllFilterCount(globalAggregateValues),
    [globalAggregateValues],
  );

  const visibleFilterOptions = useMemo(
    () =>
      getVisibleOptions([
        { title: ALL_FILTER_KEY, count: totalGlobalCount },
        { title: "New" },
        { title: "In Progress" },
        { title: "Fixed" },
        { title: "Completed" },
      ]),
    [getVisibleOptions, totalGlobalCount],
  );

  // Zero-count status pills are hidden by getVisibleAggregateOptions. If the URL/default
  // left us on a now-hidden filter (classic: New when every request is In Progress+), the
  // rows stay filtered to that empty status while hasAnyRequests is true — empty-state lie.
  // Fall back to All whenever the active filter is not among the visible pills — but only
  // after counts load (loading-time All-only visibility must not clobber dashboard deep-links).
  useEffect(() => {
    if (!shouldResolveOrphanedAggregateFilter(countsLoading, globalAggregateValues)) {
      return;
    }
    const resolved = resolveOrphanedAggregateFilter(activeFilter, visibleFilterOptions);
    if (resolved !== activeFilter) {
      setActiveFilter(resolved);
    }
  }, [countsLoading, globalAggregateValues, visibleFilterOptions, activeFilter]);

  const handleServiceRequestAdded = () => {
    fetchData(1); // Refresh service request list
    refreshCounts();
    if (onServiceRequestAdded) {
      onServiceRequestAdded(); // Trigger parent refresh
    }
  };

  useEffect(() => {
    // Tolerate a failed/in-flight counts fetch (apiHelper swallows the error) by also
    // trusting the row-list's own count, so a single failing counts call can't hide the
    // whole page behind the empty state while rows actually loaded fine.
    if (baselineAggregateValues) {
      setHasAnyRequests(resolveAllFilterCount(baselineAggregateValues) > 0 || rowCount > 0);
    } else if (rowCount > 0) {
      setHasAnyRequests(true);
    }
  }, [baselineAggregateValues, rowCount]);


  // See src/utils/roleGuards.ts: Tenant and Contractor sessions bypass the ServiceRequest
  // module-permission gate here (they're authorized by backend ownership instead).
  const isTenantOrContractor = isTenantOrContractorRole(userRole);

  if (!isTenantOrContractor && !hasAccess("ServiceRequest", 1)) {
    return <NoAccess />;
  }

  const renderContent = (content: React.ReactNode) => {
    if (isTenantOrContractor) {
      return <>{content}</>;
    }
    return (
      <PermissionGuard
        permission="ServiceRequest"
        level={AccessLevelEnum.ReadOnly}
        fallback={<NoAccess />}
      >
        {content}
      </PermissionGuard>
    );
  };

  return (
    <Routes>
      <Route
        index
        element={renderContent(
            <div className={`content-wrapper container-fluid ${parentClassName}`}>
              {hasAnyRequests && (
                <ServiceRequestTopHeader
                  PageHeading="Service Requests"
                  className={className}
                  ButtonName="Add Service Request"
                  MobileButtonName="Add"
                  showAddButton={
                    entityTypeId === EntityType.Contractor || tenantUnitId <= 0
                      ? false
                      : userRole === "Tenant" ? true : canWrite("ServiceRequest")
                  }
                  setSearchTerm={setSearchTerm}
                  searchTerm={searchTerm}
                  handleSort={handleSort}
                  currentSortDirection={sortDirection}
                  handleFilterChange={handleFilterChange}
                  filterOptions={visibleFilterOptions}
                  onClick={() => setModalOpen(true)}
                  activeFilter={activeFilter}
                  setActiveFilter={setActiveFilter}
                  isEntityTypeTenant={entityTypeId === EntityType.Tenant}
                />
              )}

              <ServiceRequestCard
                Id="ServiceRequest"
                IsFooter={true}
                Searchlength={searchTerm.length}
                dataHookState={{
                  rows,
                  // Fold in `countsLoading`: don't trust an empty row list until the aggregates
                  // fetch has also resolved at least once -- otherwise a correctly-empty default
                  // tab can flash the permanent empty state before we know whether the user has
                  // zero requests everywhere. `ServiceRequestCard`'s standardized
                  // `loading && rows.length === 0` branch treats this exactly like an in-flight
                  // row fetch and shows the centered spinner instead.
                  loading: loading || countsLoading,
                  hasMore,
                  fetchData,
                  currentPage,
                }}
                showEmptyBox={
                  entityTypeId === EntityType.Contractor ? false : true
                }
                userRole={userRole}
                hasAnyRequests={hasAnyRequests}
                onAddClick={() => setModalOpen(true)}
                entityTypeId={entityTypeId}
                onServiceRequestDeleted={refreshCounts}
                onServiceRequestVerified={refreshCounts}
              />

              {isModalOpen && (
                <AddServiceRequestForm
                  isModalOpen={isModalOpen}
                  setModalOpen={setModalOpen}
                  fetchData={handleServiceRequestAdded}
                  isEntity={isEntity}
                  entityTypeId={
                    entityTypeId === EntityType.Property
                      ? ServiceRequestEntityTypesEnum.Property
                      : entityTypeId === EntityType.Unit
                      ? ServiceRequestEntityTypesEnum.Unit
                      : entityTypeId === EntityType.Tenant
                      ? ServiceRequestEntityTypesEnum.Unit
                      : Number(entityTypeId)
                  }
                  entityId={
                    entityTypeId === EntityType.Tenant ? tenantUnitId : entityId
                  }
                />
              )}
            </div>
        )}
      />

      <Route
        path="/servicerequestdetails/:id"
        element={renderContent(<ServiceRequestDetails userRole={userRole} />)}
      />
    </Routes>
  );
};

export default ServiceRequest;